import { differenceInMilliseconds } from 'date-fns';
import { startAppListening } from '../../state/middlewares/listener';
import { getIsProduction } from '../../utils/request';
import { apiSettingsActions } from './ducks';
import { runSandboxLogin } from './utils/runSandboxLogin';
import { ApiSettingsActionTypes, SetEnvironmentAction, SetSandboxTokenAction } from './typings';

/** Refresh this long before the sandbox token expires. */
export const TOKEN_REFRESH_MARGIN_MS = 60 * 1000;
/** A token already inside the margin (or without a valid expiry) retries after a back-off that doubles up to the cap. */
export const TOKEN_REFRESH_MIN_BACKOFF_MS = 5 * 1000;
export const TOKEN_REFRESH_MAX_BACKOFF_MS = 5 * 60 * 1000;

/**
 * Registers the sandbox side effects (same behavior as the former sandboxSagas).
 */
export const startSandboxListeners = (): void => {
  // consecutive tokens that arrived already inside the refresh margin; reset by a healthy token
  let shortLivedTokens = 0;

  /**
   * Switching to development logs into the sandbox; the latest switch wins
   * (same behavior as the former triggerAuthSandbox saga).
   */
  // triggered when switching envs
  startAppListening({
    type: ApiSettingsActionTypes.SET_ENVIRONMENT,
    effect: async (action, api) => {
      api.cancelActiveListeners();
      if ((action as SetEnvironmentAction).payload === 'development') {
        // we call this function immediately, instead of firing the login event
        // This way the call gets canceled when the users switches back to production
        await api.pause(runSandboxLogin(api.signal));
      }
    },
  });

  /**
   * Logs into the sandbox, the latest start wins (same behavior as the former loginSandboxStart saga).
   */
  // triggered when we aren't authenticated anymore, but where still in sandbox mode
  startAppListening({
    type: ApiSettingsActionTypes.LOGIN_SANDBOX_START,
    effect: async (_action, api) => {
      api.cancelActiveListeners();
      await api.pause(runSandboxLogin(api.signal));
    },
  });

  /**
   * Starts a new sandbox login one minute before the latest token expires. A token that is already
   * inside that minute, or has no valid expiry, refreshes after a growing back-off so a server that
   * keeps handing out such tokens is not hammered.
   */
  // triggered when successfully obtained a token from core prod
  startAppListening({
    type: ApiSettingsActionTypes.SET_SANDBOX_TOKEN,
    effect: async (action, api) => {
      api.cancelActiveListeners();
      const expireAt = new Date((action as SetSandboxTokenAction).payload.expire_at);
      // one minute before expiring we would like to refresh
      const untilRefresh = differenceInMilliseconds(expireAt, new Date()) - TOKEN_REFRESH_MARGIN_MS;
      let wait: number;
      if (untilRefresh > 0) {
        shortLivedTokens = 0;
        wait = untilRefresh;
      } else {
        // NaN (invalid expire_at) lands here too: back off instead of re-logging in straight away
        wait = Math.min(TOKEN_REFRESH_MIN_BACKOFF_MS * 2 ** shortLivedTokens, TOKEN_REFRESH_MAX_BACKOFF_MS);
        shortLivedTokens += 1;
      }
      await api.delay(wait);
      // switched back to production meanwhile: no sandbox token to keep fresh
      if (getIsProduction()) return;
      api.dispatch(apiSettingsActions.loginSandboxStart());
    },
  });
};
